import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from 'react-router-dom';
import AppShell from './App';
import DashboardPage from './pages/DashboardPage';
import BacktestsPage from './pages/BacktestsPage';
import DataIndexPage from './pages/DataIndexPage';
import StrategiesPage from './pages/StrategiesPage';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage, { SecurityPage } from './pages/ChangePasswordPage';
import UsersPage from './pages/UsersPage';
import AdminLogsPage from './pages/AdminLogsPage';
import TradingPage from './pages/TradingPage';
import ManualTradingPage from './pages/ManualTradingPage';
import TradingDeploymentPage from './pages/TradingDeploymentPage';
import TradingConnectionsPage from './pages/TradingConnectionsPage';
import SetupPage from './pages/SetupPage';
import NotificationsPage, { AlertingLayout, BotSetup, DeliveryHealth, GlobalPolicy } from './pages/NotificationsPage';
import ForbiddenPage from './pages/ForbiddenPage';
import NotFoundPage from './pages/NotFoundPage';
import { AdminOnly, GuestOnly, RequireAuth, RequirePasswordChanged, SetupOnly, TradingOnly } from './components/auth/RouteGuards';
import { routes as paths } from './lib/routePaths';

const DatasetDetailPage = lazy(() => import('./pages/DatasetDetailPage'));
const BacktestDetailPage = lazy(() => import('./pages/BacktestDetailPage'));
const BacktestSessionPage = lazy(() => import('./pages/BacktestSessionPage'));
const StrategyBuilderPage = lazy(() => import('./pages/StrategyBuilderPage'));

function LoadingEntity({ name }: { name: string }) {
  return <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>Loading {name}...</p>;
}

const datasetDetailElement = <Suspense fallback={<LoadingEntity name="dataset" />}><DatasetDetailPage /></Suspense>;
const backtestDetailElement = <Suspense fallback={<LoadingEntity name="backtest" />}><BacktestDetailPage /></Suspense>;
const backtestSessionElement = <Suspense fallback={<LoadingEntity name="interactive session" />}><BacktestSessionPage /></Suspense>;
const strategyBuilderElement = <Suspense fallback={<LoadingEntity name="strategy editor" />}><StrategyBuilderPage /></Suspense>;

function CompatibilityRedirect({ to, parameter }: { to: string; parameter?: string }) {
  const location = useLocation();
  const params = useParams();
  const suffix = parameter && params[parameter] ? `/${encodeURIComponent(params[parameter]!)}` : '';
  return <Navigate to={`${to}${suffix}${location.search}${location.hash}`} replace />;
}

export const routes: RouteObject[] = [
  { path: '/setup', element: <SetupOnly><SetupPage /></SetupOnly> },
  { path: '/login', element: <GuestOnly><LoginPage /></GuestOnly> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/change-password', element: <ChangePasswordPage /> },
      {
        element: <RequirePasswordChanged />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'datasets', element: <DataIndexPage /> },
              { path: 'datasets/:datasetId', element: datasetDetailElement },
              { path: 'strategies', element: <StrategiesPage /> },
              { path: 'strategies/new', element: strategyBuilderElement },
              { path: 'strategies/:strategyId/edit', element: strategyBuilderElement },
              { path: 'backtests', element: <BacktestsPage /> },
              { path: 'backtests/session/:sessionId', element: backtestSessionElement },
              { path: 'backtests/:backtestId', element: backtestDetailElement },
              { path: 'settings/alerts', element: <NotificationsPage /> },
              { path: 'settings/security', element: <SecurityPage /> },
              { path: 'admin/users', element: <AdminOnly><UsersPage /></AdminOnly> },
              { path: 'admin/logs', element: <AdminOnly><AdminLogsPage /></AdminOnly> },
              {
                path: 'admin/alerting',
                element: <AdminOnly><AlertingLayout /></AdminOnly>,
                children: [
                  { index: true, element: <CompatibilityRedirect to={paths.alertingBot} /> },
                  { path: 'bot', element: <BotSetup /> },
                  { path: 'policy', element: <GlobalPolicy /> },
                  { path: 'delivery', element: <DeliveryHealth /> },
                ],
              },
              { path: 'trading', element: <TradingOnly><TradingPage /></TradingOnly> },
              { path: 'trading/manual', element: <TradingOnly><ManualTradingPage /></TradingOnly> },
              { path: 'trading/deployments/:deploymentId', element: <TradingOnly><TradingDeploymentPage /></TradingOnly> },
              { path: 'trading/connections', element: <TradingOnly><TradingConnectionsPage /></TradingOnly> },
              { path: 'forbidden', element: <ForbiddenPage /> },

              { path: 'data', element: <CompatibilityRedirect to={paths.datasets} /> },
              { path: 'data/:datasetId', element: <CompatibilityRedirect to={paths.datasets} parameter="datasetId" /> },
              { path: 'notifications', element: <CompatibilityRedirect to={paths.myAlerts} /> },
              { path: 'users', element: <CompatibilityRedirect to={paths.users} /> },
              { path: 'logs', element: <CompatibilityRedirect to={paths.logs} /> },
              { path: 'admin', element: <CompatibilityRedirect to={paths.users} /> },
              { path: 'settings', element: <CompatibilityRedirect to={paths.myAlerts} /> },

              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
